import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Icon,
  Typography,
} from "@mui/material";
import MemberStatusPieChart from "./MemberStatusPieChart";
import ProgramWiseMembers from "./ProgramWiseMembers";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { GetMembers } from "../../api/hooks";
import { IMemberStatusResult } from "../../api/types";
import MainCard from "components/MainCard";
import { BarChartOutlined } from "@ant-design/icons";
import FormLoader from "components/Containers/FormLoader";
import { IDropdownModel } from "common/models/IDropdownModel";
import { useBatchValuesQuery } from "features/testimonial/api/hooks";
import { GetProgrambyCommunity } from "features/member/api/hooks";
import { useSpecializationValuesQuery } from "features/specialization/api/hooks";
import { useCourseValuesQuery } from "features/course/api/hooks";
import { AutoCompleteElement } from "components/AutoComplete";
import { useCommunityWiseValuesQuery as useInstituteValues } from "features/institute/api/hooks";

const MemberStatistics = () => {
  const [filterModel, setFilterModel] = useState({
    filter: {
      fromDate: null,
      toDate: null,
      instituteID: null,
      courseID: null,
      specializationID: null,
      programID: null,
      year: null,
    },
  });

  const [data, setData] = useState<IMemberStatusResult>();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      fromDate: null,
      toDate: null,
      instituteID: null,
      courseID: null,
      specializationID: null,
      programID: null,
      year: null,
    },
  });

  const onSubmit = handleSubmit((filter) => {
    setFilterModel({
      filter: {
        fromDate: filter.fromDate ? filter.fromDate : null,
        toDate: filter.toDate ? filter.toDate : null,
        instituteID: filter.instituteID ? filter.instituteID : null,
        courseID: filter.courseID ? filter.courseID : null,
        specializationID: filter.specializationID
          ? filter.specializationID
          : null,
        programID: filter.programID ? filter.programID : null,
        year: filter.year ? filter.year : null,
      },
    });
  });

  const memberQuery = GetMembers(filterModel);

  //#region Institute
  const instituteQuery = useInstituteValues();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    []
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.data?.apiData || []);
  }, [instituteQuery.data?.apiData]);
  //#endregion

  //#region Course
  const courseQuery = useCourseValuesQuery();
  const [courseOptions, setCourseOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setCourseOptions(courseQuery?.data?.apiData || []);
  }, [courseQuery.data]);
  //#endregion

  //#region Specialization
  const specializationQuery = useSpecializationValuesQuery();
  const [specializationOptions, setSpecializationOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setSpecializationOptions(specializationQuery?.data?.apiData || []);
  }, [specializationQuery.data]);

  //#endregion

  //#region Program
  const programQuery = GetProgrambyCommunity();
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setProgramOptions(programQuery?.data?.apiData || []);
  }, [programQuery.data?.apiData]);
  //#endregion

  //#region Batch
  const batchQuery = useBatchValuesQuery();
  const [batchOptions, setBatchOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setBatchOptions(batchQuery?.data?.apiData || []);
  }, [batchQuery.data?.apiData]);
  //#endregion

  useEffect(() => {
    const raw = batchQuery?.data?.apiData || [];
    const formatted = raw.map((item) => ({
      id: item.name,
      name: item.name,
    }));
    setBatchOptions(formatted);
  }, [batchQuery.data?.apiData]);

  useEffect(() => {
    if (memberQuery.data?.status === 200) {
      setData(memberQuery?.data?.apiData);
    }
  }, [memberQuery.data]);

  useEffect(() => {
    memberQuery.refetch();
  }, [filterModel]);

  const handleClear = () => {
    setFilterModel((prev) => ({
      ...prev,
      filter: {
        fromDate: null,
        toDate: null,
        instituteID: null,
        courseID: null,
        specializationID: null,
        programID: null,
        year: null,
      },
    }));
    reset();
  };

  return (
    <>
      <MainCard sx={{ my: 1 }}>
        <Typography variant="h3">
          <Icon sx={{ marginRight: "5px" }}>
            <BarChartOutlined />
          </Icon>
          Members
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6} lg={3}>
              <Controller
                name="fromDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    sx={{ width: "100%" }}
                    label="From Date"
                    format="DD-MM-YYYY"
                    value={value ? dayjs(value) : null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                        fullWidth: false,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6} lg={3}>
              <Controller
                name="toDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    sx={{ width: "100%" }}
                    label="To Date"
                    format="DD-MM-YYYY"
                    value={value ? dayjs(value) : null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                        fullWidth: false,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={3}>
              <FormControl fullWidth>
                <AutoCompleteElement
                  options={instituteOptions}
                  control={control}
                  name="instituteID"
                  label="Institute / School"
                  placeholder="Select Institute / School"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={3}>
              <FormControl fullWidth>
                <AutoCompleteElement
                  options={courseOptions}
                  control={control}
                  name="courseID"
                  label="Course"
                  placeholder="Select Course"
                />
              </FormControl>
            </Grid>

            <Grid item xs={6} md={6} lg={3}>
              <FormControl fullWidth>
                <AutoCompleteElement
                  options={specializationOptions}
                  control={control}
                  name="specializationID"
                  label="Specialization"
                  placeholder="Select Specialization"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={3}>
              <FormControl fullWidth>
                <AutoCompleteElement
                  options={programOptions}
                  control={control}
                  name="programID"
                  label="Program"
                  placeholder="Select Program"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={3}>
              <FormControl fullWidth>
                {/* <SelectField
                  control={control}
                  placeholder="Select Passout Batch/Year"
                  label="Passout Batch/Year"
                  name="year"
                  options={yearOptions}
                /> */}
                <AutoCompleteElement
                  options={batchOptions}
                  control={control}
                  name="year"
                  label="Passout Batch/Year"
                  placeholder="Select Passout Batch/Year"
                />
              </FormControl>
            </Grid>
            <Grid
              item
              display={"flex"}
              alignItems={"center"}
              xs={6}
              md={4}
              lg={3}
            >
              <Button type="submit" variant="contained">
                Show
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{ ml: 1 }}
                onClick={handleClear}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>

      <Grid container spacing={2.5}>
        <Grid item sm={6} md={4} xs={12}>
          {memberQuery.isFetching ? (
            <FormLoader />
          ) : (
            <MemberStatusPieChart
              totalcount={data?.memberTotalCountResult}
              data={data?.memberChartResult}
              isFetching={memberQuery.isFetching}
            />
          )}
        </Grid>
        <Grid item sm={12} md={8} xs={12}>
          <ProgramWiseMembers data={data?.memberTableResult || []} />
        </Grid>
      </Grid>
    </>
  );
};

export default MemberStatistics;
